import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { WHATSAPP_LINK, WHATSAPP_NUMBER, openWhatsApp } from "@/data/site";
import { API } from "@/lib/api";

export default function FloatingButtons() {
    const [chatOpen, setChatOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "🙏 Namaste! I'm Dr. Tannu's Vedic assistant. Ask me about kundli, nakshatras, remedies, or which service is right for you." },
    ]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streaming]);

    const sendMessage = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || streaming) return;
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setInput("");
        setStreaming(true);
        try {
            const res = await fetch(`${API}/chat/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text }),
            });
            if (!res.body) throw new Error("No stream body");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split("\n");
                buf = lines.pop() || "";
                for (const line of lines) {
                    if (line.startsWith("event: session")) {
                        // next line is data
                    } else if (line.startsWith("data: ")) {
                        const payload = line.slice(6);
                        if (!sessionId && payload && !payload.includes("end") && payload.length < 60 && payload.match(/^[a-f0-9-]+$/i)) {
                            setSessionId(payload);
                        } else if (payload && payload !== "end") {
                            const chunk = payload.replace(/\\n/g, "\n");
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
                                return copy;
                            });
                        }
                    }
                }
            }
        } catch (err) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again or WhatsApp us directly." };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    return (
        <>
            {/* WhatsApp floating — positioned above the Emergent badge so it never gets covered */}
            <a
                href={WHATSAPP_LINK}
                onClick={openWhatsApp}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
                data-testid="whatsapp-float"
                className="fixed bottom-20 right-6 z-[60] h-14 w-14 grid place-items-center rounded-full bg-[#25D366] text-white shadow-lg hover:scale-110 transition-transform"
            >
                <svg viewBox="0 0 32 32" className="h-7 w-7" fill="currentColor"><path d="M16 .5C7.4.5.5 7.4.5 16c0 2.8.74 5.5 2.14 7.9L0 32l8.4-2.2A15.5 15.5 0 0 0 16 31.5C24.6 31.5 31.5 24.6 31.5 16S24.6.5 16 .5zm0 28.3c-2.4 0-4.74-.64-6.78-1.84l-.48-.28-5 1.32 1.34-4.86-.32-.5A12.79 12.79 0 0 1 3.18 16C3.18 8.9 8.9 3.18 16 3.18S28.82 8.9 28.82 16 23.1 28.82 16 28.82zm7.06-9.6c-.38-.2-2.26-1.12-2.62-1.24-.34-.12-.6-.2-.84.2-.26.38-.96 1.24-1.18 1.5-.22.26-.44.28-.82.1-.38-.2-1.6-.6-3.04-1.88a11.4 11.4 0 0 1-2.1-2.6c-.22-.38 0-.58.18-.78.18-.18.4-.46.6-.7.2-.26.26-.42.4-.7.14-.28.06-.52-.04-.72-.1-.2-.84-2.02-1.16-2.78-.3-.72-.62-.62-.84-.62h-.72c-.24 0-.64.1-.98.48-.34.38-1.3 1.28-1.3 3.12 0 1.84 1.34 3.62 1.52 3.88.2.26 2.64 4.04 6.4 5.66.9.38 1.6.62 2.14.78.9.28 1.72.24 2.36.14.72-.1 2.26-.92 2.58-1.82.32-.9.32-1.66.22-1.82-.1-.16-.36-.26-.74-.46z" /></svg>
            </a>

            {/* AI chat trigger */}
            <button
                onClick={() => setChatOpen((v) => !v)}
                aria-label="AI Astrology Assistant"
                data-testid="chat-toggle"
                className="fixed bottom-6 left-6 z-50 h-14 w-14 grid place-items-center rounded-full bg-gold-gradient text-ink shadow-lg hover:scale-110 transition-transform"
            >
                {chatOpen ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
            </button>

            {chatOpen && (
                <div data-testid="chat-panel" className="fixed bottom-24 left-6 z-50 w-[92vw] max-w-md h-[70vh] max-h-[600px] bg-ink-surface/95 backdrop-blur-xl border border-gold/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-gold/20 flex items-center gap-3 bg-ink-deep/80">
                        <div className="h-10 w-10 rounded-full bg-gold-gradient grid place-items-center text-ink">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-serif text-cream text-lg leading-none">Vedic Assistant</div>
                            <div className="text-[11px] text-gold tracking-widest uppercase">By VedicByTannu</div>
                        </div>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-gold text-ink"
                                        : "bg-ink-deep/80 text-cream border border-gold/15"
                                }`}>
                                    {m.content || <span className="opacity-60">...</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={sendMessage} className="border-t border-gold/20 p-3 flex gap-2 bg-ink-deep/80">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask about your kundli, dasha, remedies..."
                            className="input-luxe text-sm flex-1"
                            data-testid="chat-input"
                            disabled={streaming}
                        />
                        <button type="submit" className="h-11 w-11 grid place-items-center rounded-lg bg-gold text-ink disabled:opacity-50" disabled={streaming || !input.trim()} data-testid="chat-send">
                            <Send className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
